import React, { useState, useEffect } from 'react';
import { Icon } from '../Icon/Icon';
import { IconName } from '../Icon/Icon.type';
import { Text } from '../Text/Text';
import { Button } from '../Button/Button';
import { Input } from '../Input/Input';
import { versionControlApi } from '../../services/versionControlApi';
import type { IGitRemoteSharePanelProps } from './GitRemoteSharePanel.type';

export const GitRemoteSharePanel: React.FC<IGitRemoteSharePanelProps> = ({
  projectId,
  isOpen,
  isClosing,
  onClose,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<{ cloned_as: string; clone_url: string; repo_url?: string; permission?: string } | null>(null);
  const [tokenCopied, setTokenCopied] = useState(false);
  const [urlCopied, setUrlCopied] = useState(false);
  const [repoUrlCopied, setRepoUrlCopied] = useState(false);
  const [resetting, setResetting] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    try {
      const result = await versionControlApi.gitClone(projectId);
      setData(result);
    } catch (error) {
      console.error('Failed to fetch git remote info:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && !data) {
      fetchData();
    }
  }, [isOpen, data]);

  const handleResetToken = async () => {
    if (!window.confirm('Are you sure you want to generate a new token? The old one will stop working immediately.')) {
        return;
    }
    
    setResetting(true);
    try {
      await versionControlApi.resetGitToken(projectId);
      await fetchData(); // Refresh data to get the new token in the URL
    } catch (error) {
      console.error('Failed to reset token:', error);
      alert('Failed to reset token. Please try again.');
    } finally {
      setResetting(false);
    }
  };

  const handleCopyToken = async () => {
    if (!data?.clone_url) return;
    try {
      // Extract token from URL: http://user:token@host/...
      const url = new URL(data.clone_url);
      const token = url.password;
      await navigator.clipboard.writeText(token);
      setTokenCopied(true);
      setTimeout(() => setTokenCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy token:', error);
    }
  };

  const handleCopyUrl = async () => {
    if (!data?.clone_url) return;
    try {
      await navigator.clipboard.writeText(data.clone_url);
      setUrlCopied(true);
      setTimeout(() => setUrlCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy URL:', error);
    }
  };

  const handleCopyRepoUrl = async () => {
    if (!data?.repo_url) return;
    try {
      await navigator.clipboard.writeText(data.repo_url);
      setRepoUrlCopied(true);
      setTimeout(() => setRepoUrlCopied(false), 2000);
    } catch (error) {
      console.error('Failed to copy repo URL:', error);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-start justify-end bg-alpha-black-70 ${isClosing ? 'animate-fade-out' : 'animate-fade-in'}`}
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-labelledby="manage-git-remote-title"
    >
      <div
        className={`absolute right-0 top-0 h-full bg-bg-primary w-[400px] flex flex-col border-l border-border-secondary-alt ${isClosing ? 'animate-slide-out-right' : 'animate-slide-in-right'}`}
        onClick={(e) => e.stopPropagation()}
        style={{
          boxShadow: "0 20px 24px -4px rgba(10, 13, 18, 0.08), 0 8px 8px -4px rgba(10, 13, 18, 0.03), 0 3px 3px -1.5px rgba(10, 13, 18, 0.04)"
        }}
      >
        {/* Top Section */}
        <div className="flex p-bg-v2-3xl items-center gap-bg-v2-md self-stretch justify-between">
          <div>
            <Text variant="text-xl" weight="bold">Manage Git Remote</Text>
          </div>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-full text-fg-secondary hover:bg-bg-primary-hover hover:text-text-secondary-hover transition-all duration-200"
            aria-label="Close modal"
          >
            <Icon name={IconName.Cross} size="lg" inheritColor />
          </button>
        </div>

        {/* Divider */}
        <div className="box-border flex flex-col gap-[16px] items-center px-[var(--spacing-3xl,24px)] py-0 w-full">
          <div className="bg-border-secondary h-px w-full" />
        </div>

        {/* Content */}
        <div className="flex flex-col items-center gap-bg-v2-3xl self-stretch px-bg-v2-3xl py-bg-v2-3xl overflow-y-auto flex-1">
          {loading ? (
            <div className="w-full flex justify-center py-20">
                <Icon name={IconName.Reload} className="animate-spin text-fg-tertiary" size="xl" inheritColor />
            </div>
          ) : (
            <div className="w-full space-y-6">
              {/* Info Section
              <div className="bg-bg-secondary-subtle border border-border-secondary rounded-bg-v2-md p-bg-v2-lg">
                <div className="flex items-center gap-bg-v2-md mb-bg-v2-sm">
                   <Text variant="text-sm" weight="semibold" color="primary">Security Information</Text>
                </div>
                <Text variant="text-xs" color="secondary">
                   This token grants {data?.permission === 'write' ? 'read/write' : 'read-only'} access to this specific repository. Do not share it publicly.
                </Text>
              </div> */}

              {/* Username Section */}
              <Input
                id="username"
                label="Username"
                value={data?.cloned_as || ''}
                readOnly
                className="w-full"
              />

              {/* Token Section */}
              <div className="w-full space-y-2">
                <Input
                  id="access-token"
                  label="Access Token"
                  value="****************************************"
                  readOnly
                  className="w-full"
                  endIcon={<Icon name={tokenCopied ? IconName.Check : IconName.Copy} size="md" />}
                  onEndIconClick={handleCopyToken}
                />
                <div className="flex justify-between items-center w-full mt-1">
                  <div>
                    {tokenCopied && <Text variant="text-xs" color="brand-primary">✓ Token copied</Text>}
                  </div>
                  <button
                    type="button"
                    onClick={handleResetToken}
                    disabled={resetting}
                    className="text-bg-v2-sm font-bg-v2-medium text-text-brand-primary hover:text-text-brand-secondary transition-smooth disabled:opacity-50 underline-offset-4 hover:underline"
                  >
                    {resetting ? 'Resetting...' : 'Having Trouble with Token? Reset It'}
                  </button>
                </div>
              </div>

              {/* Repo URL Section */}
              <div className="w-full space-y-2">
                <Input
                  id="repo-url"
                  label="Repo URL"
                  value={data?.repo_url || ''}
                  readOnly
                  className="w-full"
                  endIcon={<Icon name={repoUrlCopied ? IconName.Check : IconName.Copy} size="md" />}
                  onEndIconClick={handleCopyRepoUrl}
                />
                <div className="flex mt-1">
                  {repoUrlCopied && <Text variant="text-xs" color="brand-primary">✓ Repo URL copied</Text>}
                </div>
              </div>

              {/* Clone URL Section */}
              <div className="w-full space-y-2">
                <Input
                  id="clone-url"
                  label="Clone URL"
                  value={data?.clone_url ? data.clone_url.replace(/:[^@:]+@/, ':******@') : ''}
                  readOnly
                  className="w-full"
                  endIcon={<Icon name={urlCopied ? IconName.Check : IconName.Copy} size="md" />}
                  onEndIconClick={handleCopyUrl}
                />
                <div className="flex mt-1">
                  {urlCopied && <Text variant="text-xs" color="brand-primary">✓ URL copied</Text>}
                </div>
              </div>

            </div>
          )}
        </div>
      </div>
    </div>
  );
};
